import { Suspense, lazy, type ReactNode } from 'react';
import { I18nProvider } from '@client-app/i18n';
import { createBrowserRouter, Navigate, useLocation } from 'react-router-dom';
import AdminLayout from './Admin';
import { runtimeAccessConfig } from './config/runtimeAccess';
import { AppProvider, CloudAgentProvider } from './contexts';
import Auth from './pages/Auth';
import AuthError from './pages/Auth/Error';
import OnboardingPage from './pages/Onboarding';
import OnboardingFailurePage from './pages/OnboardingFailure';
import OnboardingSuccessPage from './pages/OnboardingSuccess';
import OAuthClientReturnPage from './pages/OAuthClientReturn';
import OAuthSuccessPage from './pages/OAuthSuccess';
import PaymentReturnPage from './pages/PaymentReturn';
import JudgeMeOAuthCallbackPage from './pages/JudgeMeOAuthCallback';
import McpOAuthCallbackPage from './pages/McpOAuthCallback';
import HomePage from './pages/Home';
import { SettingsShellPage } from './pages/SettingsPages';
import OwnerTransferAcceptPage from './pages/OwnerTransferAccept';
import InviteAcceptPage from './pages/InviteAccept';


const CloudAgentPage = lazy(() => import('./pages/CloudAgent'));

const AdminIndexRedirect = () => {
    const location = useLocation();

    return <Navigate to={`/profile${location.search}${location.hash}`} replace />;
};

const LegacyLaunchRedirect = () => {
    const location = useLocation();

    return <Navigate to={`/profile${location.search}${location.hash}`} replace />;
};

const StaticI18nRoute = ({ children }: { children: ReactNode }) => (
    <I18nProvider>
        {children}
    </I18nProvider>
);

const cloudRuntimeFallback = (
    <div style={{ width: '100%', minHeight: '100vh', background: '#fff' }} />
);

const cloudRuntimeRoutes = runtimeAccessConfig.disableCloudRuntime
    ? []
    : [
        {
            path: '/cloud-agent',
            element: (
                <Suspense fallback={cloudRuntimeFallback}>
                    <CloudAgentProvider>
                        <CloudAgentPage />
                    </CloudAgentProvider>
                </Suspense>
            ),
        },
        {
            index: true,
            element: (
                <Suspense fallback={cloudRuntimeFallback}>
                    <CloudAgentProvider>
                        <CloudAgentPage />
                    </CloudAgentProvider>
                </Suspense>
            ),
        },
    ];

const disabledCloudRuntimeAdminRoutes = runtimeAccessConfig.disableCloudRuntime
    ? [
        {
            path: '/cloud-agent',
            element: <HomePage />,
        },
    ]
    : [];

export const router = createBrowserRouter([

    // 静态页面 - 不需要 Provider，避免触发接口调用
    {
        path: '/integrations/success',
        element: <OnboardingSuccessPage />
    },
    {
        path: '/onboarding/failure',
        element: <StaticI18nRoute><OnboardingFailurePage /></StaticI18nRoute>
    },
    {
        path: '/invite',
        element: <StaticI18nRoute><InviteAcceptPage /></StaticI18nRoute>
    },
    {
        path: '/judgeme/oauth/callback',
        element: <JudgeMeOAuthCallbackPage />
    },
    {
        path: '/mcp/oauth/callback',
        element: <StaticI18nRoute><McpOAuthCallbackPage /></StaticI18nRoute>
    },

    {
        path: '/',
        element: <AppProvider />,
        children: [
            {
                path: '/auth',
                element: <Auth />
            },
            {
                path: '/auth/error',
                element: <AuthError />
            },
            {
                path: '/error',
                element: <AuthError />
            },
            {
                path: '/oauth/success',
                element: <OAuthSuccessPage />
            },
            {
                path: '/oauth/client-return',
                element: <OAuthClientReturnPage />
            },
            {
                path: '/onboarding',
                element: <OnboardingPage />
            },
            {
                path: '/payment/return',
                element: <PaymentReturnPage />
            },
            {
                path: '/launch',
                element: <LegacyLaunchRedirect />,
            },
            {
                path: '/owner-transfer/accept',
                element: <OwnerTransferAcceptPage />
            },
            ...cloudRuntimeRoutes,
            {
                element: <AdminLayout />,
                children: [
                    runtimeAccessConfig.disableCloudRuntime
                        ? {
                            index: true,
                            element: <AdminIndexRedirect />,
                        }
                        : {
                            path: '/home',
                            element: <AdminIndexRedirect />,
                    },
                    {
                        path: '/:settingsTab',
                        element: <SettingsShellPage />,
                    },
                    ...disabledCloudRuntimeAdminRoutes,
                ]
            },
        ]
    }
], {basename: '/admin'});
